import classNames from 'classnames';
import { useCopyTextToClipboard } from 'hooks/useCopyToClipboard';
import type { IntlKey } from 'locales';
import { FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

type CopyButtonsProps = {
  className: string;
  isDisabled: boolean;
  selectedUnderlyingsIds: string[];
  tooltipKey?: IntlKey;
};

export const CopyButton: FC<CopyButtonsProps> = ({ className, isDisabled, selectedUnderlyingsIds, tooltipKey }) => {
  const generateUnderlyingsAsTextList = useCallback(() => selectedUnderlyingsIds.join('\n'), [selectedUnderlyingsIds]);
  const onCopy = useCopyTextToClipboard();

  return (
    <SmallButton
      className={className}
      iconName="file_copy"
      isDisabled={isDisabled ?? false}
      onClick={() => onCopy(generateUnderlyingsAsTextList())}
      e2e="multiple-underlyings-copy-button"
      tooltipKey={tooltipKey}
      iconSize="icon-xs"
    />
  );
};

type SmallButtonProps = {
  className: string;
  isDisabled?: boolean;
  onClick: () => void;
  iconName: string;
  e2e?: string;
  tooltipKey?: IntlKey;
  iconSize?: string;
};

export const SmallButton: FC<SmallButtonProps> = ({
  className,
  isDisabled,
  onClick,
  iconName,
  e2e,
  tooltipKey,
  iconSize,
}) => {
  const { formatMessage } = useIntl();
  const tooltip = useMemo(
    () => (tooltipKey !== undefined ? formatMessage({ id: tooltipKey }) : undefined),
    [formatMessage, tooltipKey],
  );
  return (
    <button
      tabIndex={-1}
      type="button"
      data-e2e={e2e}
      className={classNames('btn btn-sm', className)}
      disabled={isDisabled}
      onClick={onClick}
      title={tooltip}
    >
      <i className={`icon ${iconSize ? iconSize : 'icon-sm'}`}>{iconName}</i>
    </button>
  );
};
